import React, { useState, useEffect, useCallback, useRef } from "react";
import AdminHeader from "../components/AdminHeader";
import { listConversations, getChat, replyChat } from "../lib/chat";
import { Send, MessageSquare } from "lucide-react";

export default function ChatAdmin() {
  const [convos, setConvos] = useState([]);
  const [cid, setCid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const endRef = useRef(null);

  const loadConvos = useCallback(async () => {
    try { setConvos(await listConversations()); } catch (e) {}
  }, []);

  const loadMsgs = useCallback(async () => {
    if (!cid) return;
    try { setMsgs(await getChat(cid)); } catch (e) {}
  }, [cid]);

  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 5000); return () => clearInterval(t); }, [loadConvos]);
  useEffect(() => { loadMsgs(); const t = setInterval(loadMsgs, 3000); return () => clearInterval(t); }, [loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const text = draft.trim();
    if (!text || !cid) return;
    setDraft("");
    setMsgs((m) => [...m, { id: `tmp-${Date.now()}`, sender: "staff", text, at: new Date().toISOString() }]);
    try { await replyChat(cid, text); await loadMsgs(); await loadConvos(); } catch (e) {}
  };

  const active = convos.find((c) => c.id === cid);

  return (
    <div className="min-h-screen bg-[#09090B] p-4 md:p-6">
      <AdminHeader title="MESSAGES" accent="#00F0FF" />
      <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-160px)]">
        {/* conversations */}
        <div className="bg-[#18181B] border border-white/10 rounded-2xl overflow-y-auto">
          {convos.length === 0 && <div className="p-6 text-[#71717A] text-center">No messages yet.</div>}
          {convos.map((c) => (
            <button key={c.id} data-testid={`convo-${c.id}`} onClick={() => setCid(c.id)}
              className={`w-full text-left px-4 py-3 border-b border-white/5 ${cid === c.id ? "bg-[#27272A]" : "hover:bg-[#1f1f23]"}`}>
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-white">{c.name || "Guest"}</span>
                <span className="text-[#52525B] text-xs">{c.last_at ? new Date(c.last_at).toLocaleTimeString() : ""}</span>
              </div>
              <p className="text-[#71717A] text-sm truncate">{c.last_text}</p>
            </button>
          ))}
        </div>

        {/* thread */}
        <div className="bg-[#18181B] border border-white/10 rounded-2xl flex flex-col min-h-0">
          {!cid ? (
            <div className="flex-1 flex flex-col items-center justify-center text-[#52525B]">
              <MessageSquare className="w-12 h-12 mb-3" /> Select a conversation
            </div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-white/10 font-display font-bold text-white">{active?.name || "Guest"}</div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.sender === "staff" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-2xl ${m.sender === "staff" ? "bg-[#00F0FF] text-black" : "bg-[#27272A] text-white"}`}>
                      <p className="whitespace-pre-wrap break-words">{m.text}</p>
                      <p className={`text-[10px] mt-1 ${m.sender === "staff" ? "text-black/50" : "text-white/40"}`}>{new Date(m.at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-white/10 flex gap-2">
                <input data-testid="chat-reply-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  className="flex-1 h-12 rounded-xl bg-[#09090B] border border-white/10 px-4 text-white outline-none focus:border-[#00F0FF]" placeholder="Type a reply…" />
                <button data-testid="chat-reply-send" onClick={send} className="h-12 px-5 rounded-xl bg-[#00F0FF] text-black font-bold flex items-center gap-2"><Send className="w-5 h-5" /> Send</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
